"use client";

import React, { useState, useRef } from "react";
import { Box, Typography, Paper, Card } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";

const FileUpload = ({
  onFileSelect,
  onFileRemove,
  multiple = true,
  accept,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);

      // Optional: Clear the input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (files) => {
    const selectedFiles = multiple ? Array.from(files) : [files[0]];
    const newFiles = selectedFiles.filter(Boolean).map((file) => ({
      file,
      id: generateUniqueId(),
    }));

    setUploadedFiles((prev) => (multiple ? [...prev, ...newFiles] : newFiles));

    if (onFileSelect) {
      onFileSelect(files);
    }
  };

  const generateUniqueId = () => {
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleDeleteFile = (id) => {
    const removedFile = uploadedFiles.find((file) => file.id === id);
    setUploadedFiles((prev) => prev.filter((file) => file.id !== id));
    onFileRemove?.(removedFile?.file);
  };

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split(".").pop()?.toLowerCase();

    if (
      ["jpg", "jpeg", "png", "gif", "svg", "webp"].includes(extension || "")
    ) {
      return <ImageIcon fontSize="small" />;
    }

    return <InsertDriveFileIcon fontSize="small" />;
  };

  return (
    <>
      <Card
        elevation={0}
        sx={{
          borderRadius: 1,
          p: 2,
          width: "100%",
          maxWidth: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          cursor: "pointer",
          backgroundColor: "white",
          "&:hover": {
            backgroundColor: "#f5f5f5",
          },
          transition: "background-color 0.3s",
        }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        className="upload-file-paper"
      >
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: "none" }}
          onChange={handleFileSelect}
          multiple={multiple}
          accept={accept}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <CloudUploadIcon
            sx={{
              fontSize: 30,
              color: "primary.main",
              mb: 1,
            }}
          />

          {uploadedFiles.length === 0 && (
            <Box>
              <Typography component="span" sx={{ display: "inline" }}>
                <Typography
                  component="span"
                  sx={{ fontWeight: 500 }}
                  className="text-black"
                >
                  Перетягніть файл{" "}
                  <Typography component="span" color="text.secondary">
                    сюди <br /> або натисніть для вибору
                  </Typography>
                </Typography>
              </Typography>
            </Box>
          )}
        </Box>
      </Card>

      {/* Upload file list */}
      {uploadedFiles.map((fileItem) => (
        <Box
          key={fileItem.id}
          sx={{
            mt: 1,
            p: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderRadius: 1,
            width: "100%",
            maxHeight: 40,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "4px",
                flexShrink: 0,
              }}
            >
              {getFileIcon(fileItem.file.name)}
            </Box>

            <Typography
              sx={{
                fontSize: "12px",
                maxWidth: "110px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={fileItem.file.name}
            >
              {fileItem.file.name}
            </Typography>
          </Box>

          <Box
            sx={{ cursor: "pointer", ":hover": { color: "error.main" } }}
            onClick={() => handleDeleteFile(fileItem.id)}
          >
            <DeleteIcon fontSize="small" />
          </Box>
        </Box>
      ))}
    </>
  );
};

export default FileUpload;
